/**
 * navSettingsStore — the navmesh's build PARAMETERS and the visibility of every
 * navigation debug layer (ENG-591, ENG-1050).
 *
 * It used to be the state of a floating "Build Navmesh" panel that owned the
 * capsule params, the Build button and four SHOW checkboxes. That panel is gone:
 * the params and the build action live on the outliner's NAVMESH row (they ARE
 * the navmesh's properties) and each debug layer is a row with its own toggle. So
 * there is no `open` any more, and with it went the `show`/`pin` split — nothing
 * is scoped to a panel session, so one flag per layer says whether it is drawn.
 *
 * The baked-artifact layers default OFF, which is what shipped: they were
 * default-true but gated on the panel being open, so nothing drew until someone
 * asked. `walkPath` is the exception and defaults ON — it is the immediate
 * feedback for a right-click "Walk here" (the click is answered on screen before
 * the generation returns), so hiding it by default would remove the one thing
 * that makes the wait legible.
 *
 * Module singleton + subscribe, shared by the outliner rows, the in-Canvas
 * navmesh overlay, the voxel volume, the capsule gizmo and the walk-here route.
 */
let state = {
  params: {
    // Agent capsule — drives the splat-transform carve: radius -> walkable
    // erosion, height -> standing clearance (UE walkableRadius/walkableHeight).
    // Defaults ~ Myra (head probe at 1.4 m -> ~1.7 m tall).
    agentRadius: 0.25, // m
    agentHeight: 1.7,  // m
    navCell: 0.2,      // navmesh grid resolution (m)
  },
  // Debug layers: is each one drawn right now? One writer per layer (its own
  // outliner row toggle), so there is nothing to reconcile between controls.
  show: {
    navmesh: false,   // blue walkable graph + active path
    voxels: false,    // orange voxel-surface cubes (the granular grid)
    collision: false, // baked collision mesh
    capsule: false,   // green agent capsule (the params above, visualised)
    walkPath: true,   // right-click "Walk here" route line + destination ring
  },
  // How the collision mesh is drawn (the COLLISION row's properties).
  style: {
    // 'shaded' = flat-shaded translucent faces (reads like the source guide's
    //            cubes — you see only the near surface). 'wireframe' = edges.
    collisionMode: 'shaded',
    // Always-on-top: depthTest off so the splat never occludes it. Off by
    // default (depth-tested reads clean); on for "draw over the splat".
    collisionOnTop: false,
  },
}
const listeners = new Set()
function emit() {
  if (typeof window !== 'undefined') window.__NAV_SETTINGS__ = state
  for (const fn of listeners) fn()
}

// Publish at module load, not only on the first write: the panel used to mount
// and mutate early, so `window.__NAV_SETTINGS__` happened to exist by the time
// anyone looked. With the panel gone nothing writes until a user clicks, and a
// debug global that is undefined until then reads as "the feature isn't loaded".
if (typeof window !== 'undefined') window.__NAV_SETTINGS__ = state

export const navSettingsStore = {
  getState() { return state },
  subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn) },
  setParams(patch) { state = { ...state, params: { ...state.params, ...patch } }; emit() },
  setShow(patch) { state = { ...state, show: { ...state.show, ...patch } }; emit() },
  setStyle(patch) { state = { ...state, style: { ...state.style, ...patch } }; emit() },

  /** Is this debug layer drawn right now? */
  isDebugOn(layer) { return !!state.show[layer] },

  /**
   * Flip one debug layer. An unknown layer THROWS rather than silently doing
   * nothing: a toggle wired to a typo would otherwise ship as a dead control.
   */
  setDebug(layer, next) {
    if (!(layer in state.show)) throw new Error('[navSettings] unknown debug layer "' + layer + '"')
    state = { ...state, show: { ...state.show, [layer]: !!next } }
    emit()
  },
}
